import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import './ActionBar.css'
import { AddOutlined, DeleteOutlined, EditOutlined, LayersOutlined } from '@mui/icons-material'
import { useState } from 'react'
import { productTypes } from '../lists'
import Bundle from './Bundle'
import { classType, lineItemType, shipToType } from '../types'
import { CurrencyInput, DatePicker, NetsuiteSearch, NumberField } from '../helper'
import { fieldLengths } from '../fieldLengths'
import dayjs from 'dayjs'

export default function ActionBar(props: {
  contractClassOptions?: classType[]
  lineItems: lineItemType[]
  setLineItems: (lineItems: lineItemType[]) => void
  totalCount: number
  selectedLineItems: lineItemType[]
  headerPurchaseVehicle: string
  setSelectedLineItems: (lineItems: lineItemType[]) => void
  onCreateBundle?: (mergedLine: lineItemType) => void
  onDeleteSelected?: () => void
  onBulkEdit?: (
    purchaseVehicleVal: string,
    productTypeVal: string,
    vendorVal: string,
    popStartDate: Date | null,
    popEndDate: Date | null,
  ) => void
  onBulkEditShippingAddress?: (addressId: number) => void
  onAddLineToKit?: (kit: string) => void
  onCreateNewKit?: (
    productDescription: string | null,
    mpn: string | null,
    quantity: number | null,
    unitPrice: number | null,
    totalPrice: number | null,
    displayPartNumber: string | null,
  ) => void
  shippingAddresses: (shipToType & { id: number })[]
  bulkActionsOnly?: boolean // only bulk edit and delete
}) {
  const [openMenu, setOpenMenu] = useState<
    ('bundle' | 'add' | 'create' | 'edit' | 'delete' | 'kit-warning' | 'kit-create') | null
  >(null)
  const [classValue, setClassValue] = useState('')
  const [productTypeValue, setProductTypeValue] = useState('')
  const [vendorValue, setVendorValue] = useState('')
  const [kitValue, setKitValue] = useState('')
  const bulkOnly = props.bulkActionsOnly === true
  const kitHeaders = props.lineItems.filter(item => item.Kit && item.Number === -1)
  const [zeroKitLineItems, setZeroKitLineItems] = useState(false)
  const [productDescription, setProductDescription] = useState('')
  const [mpn, setMpn] = useState('')
  const [quantity, setQuantity] = useState(0)
  const [unitPrice, setUnitPrice] = useState(0)
  const [totalPrice, setTotalPrice] = useState(0)
  const [shippingAddress, setShippingAddress] = useState('')
  const [popStartDate, setPopStartDate] = useState<Date | null>(null)
  const [popEndDate, setPopEndDate] = useState<Date | null>(null)
  const [displayPartNumber, setDisplayPartNumber] = useState('')
  return (
    <div
      className="action-bar"
      style={{
        opacity: props.selectedLineItems.length === 0 ? 0 : 1,
        visibility: props.selectedLineItems.length === 0 ? 'hidden' : 'visible',
      }}
    >
      <Typography variant="h6">
        {props.selectedLineItems.length} of {props.totalCount} selected
      </Typography>
      <Divider orientation="vertical" flexItem />
      {!bulkOnly && (
        <>
          <Button
            variant="text"
            color="inherit"
            startIcon={<LayersOutlined />}
            size="large"
            onClick={() => setOpenMenu('bundle')}
          >
            Bundle
          </Button>
          <Button
            variant="text"
            color="inherit"
            startIcon={<AddOutlined />}
            size="large"
            onClick={() => {
              setOpenMenu('add')
            }}
          >
            Add Line to Kit
          </Button>
          <Button
            variant="text"
            color="inherit"
            startIcon={<AddOutlined />}
            size="large"
            onClick={() => {
              if (props.selectedLineItems.filter(item => item.Kit).length > 0) {
                setOpenMenu('kit-warning')
              } else {
                setOpenMenu('kit-create')
              }
            }}
          >
            Create New Kit
          </Button>
        </>
      )}
      <Button
        variant="text"
        color="inherit"
        startIcon={<EditOutlined />}
        size="large"
        onClick={() => setOpenMenu('edit')}
      >
        Bulk Edit
      </Button>
      <Button
        variant="text"
        color="inherit"
        startIcon={<DeleteOutlined />}
        size="large"
        onClick={() => setOpenMenu('delete')}
      >
        Delete
      </Button>

      {!bulkOnly && (
        <Bundle
          open={openMenu === 'bundle'}
          setOpen={() => setOpenMenu(null)}
          selectedLineItems={props.selectedLineItems}
          setSelectedLineItems={props.setSelectedLineItems}
          onCreateBundle={props.onCreateBundle}
        />
      )}

      <Dialog
        open={openMenu === 'edit'}
        onClose={() => setOpenMenu(null)}
        fullWidth
        maxWidth="sm"
        className="action-bar-edit-dialog"
      >
        <DialogTitle variant="h6">Bulk Edit</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {props.selectedLineItems.length > 1 ? `${props.selectedLineItems.length} items have` : '1 item has'} been
            selected for bulk edit.
          </Typography>
          <TextField
            label="Contract #"
            value={classValue}
            onChange={e => setClassValue(e.target.value)}
            select
            fullWidth
            size="small"
          >
            {props.contractClassOptions?.map(cls => (
              <MenuItem key={cls.id} value={cls.name}>
                {cls.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Product Type"
            value={productTypeValue}
            onChange={e => setProductTypeValue(e.target.value)}
            select
            fullWidth
            size="small"
          >
            {productTypes.map(productType => (
              <MenuItem key={productType} value={productType}>
                {productType}
              </MenuItem>
            ))}
          </TextField>
          <NetsuiteSearch table="vendor" value={vendorValue} onChange={value => setVendorValue(value ?? '')} />
          <TextField
            label="Ship to Address"
            value={shippingAddress}
            onChange={e => setShippingAddress(e.target.value)}
            select
            fullWidth
            size="small"
          >
            {props.shippingAddresses.map(address => (
              <MenuItem key={address.id} value={String(address.id)}>
                {[
                  address.ShipToAttention,
                  address.ShipToCompany,
                  address.ShipToAddress1,
                  address.ShipToAddress2,
                  address.ShipToAddressCity,
                  address.ShipToAddressState,
                  address.ShipToAddressZip,
                  address.ShipToAddressCountry,
                ]
                  .filter(Boolean)
                  .join(' ')}
              </MenuItem>
            ))}
          </TextField>
          <div className="action-bar-edit-dialog-pop-dates-container">
            <DatePicker
              size="small"
              label="PoP Start Date"
              value={popStartDate ? dayjs.utc(popStartDate) : null}
              onChange={value => setPopStartDate(value?.toDate() ?? null)}
            />
            <DatePicker
              size="small"
              label="PoP End Date"
              value={popEndDate ? dayjs.utc(popEndDate) : null}
              onChange={value => setPopEndDate(value?.toDate() ?? null)}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setOpenMenu(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              props.onBulkEdit?.(classValue, productTypeValue, vendorValue, popStartDate, popEndDate)
              if (shippingAddress !== '') {
                props.onBulkEditShippingAddress?.(parseInt(shippingAddress, 10))
              }
              setClassValue('')
              setProductTypeValue('')
              setShippingAddress('')
              setOpenMenu(null)
              setVendorValue('')
            }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openMenu === 'delete'} onClose={() => setOpenMenu(null)} maxWidth="xs" fullWidth>
        <DialogTitle variant="h6">Delete selected lines?</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {bulkOnly
              ? props.selectedLineItems.length > 1
                ? `Delete ${props.selectedLineItems.length} selected line items? You can adjust line items again from the previous step before submitting.`
                : 'Delete the selected line item? You can adjust line items again from the previous step before submitting.'
              : props.selectedLineItems.length > 1
              ? `Delete ${props.selectedLineItems.length} selected line items? This can be reverted with Reset until you save.`
              : 'Delete the selected line item? This can be reverted with Reset until you save.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setOpenMenu(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              props.onDeleteSelected?.()
              setOpenMenu(null)
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {!bulkOnly && (
        <Dialog open={openMenu === 'add'} onClose={() => setOpenMenu(null)} maxWidth="xs" fullWidth>
          <DialogTitle variant="h6">Add Line to Kit</DialogTitle>
          <DialogContent className="action-bar-add-to-kit-dialog-content">
            {props.selectedLineItems.filter(item => item.Kit).length > 0 && (
              <Alert severity="warning">
                {props.selectedLineItems.filter(item => item.Kit).length} line item
                {props.selectedLineItems.filter(item => item.Kit).length > 1 ? 's are' : ' is'} already in a kit.
              </Alert>
            )}
            <Typography variant="body1">Select a kit to add the line to.</Typography>
            <TextField
              select
              fullWidth
              size="small"
              label="Kit"
              value={kitValue}
              onChange={e => setKitValue(e.target.value)}
            >
              {kitHeaders.map(header => (
                <MenuItem key={header.Kit ?? ''} value={header.Kit ?? ''}>
                  {header.MfrPartNum ?? header.Kit}
                </MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setOpenMenu(null)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                props.onAddLineToKit?.(kitValue)
                setKitValue('')
                setOpenMenu(null)
              }}
            >
              Add Line to Kit
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {!bulkOnly && (
        <Dialog open={openMenu === 'kit-create'} onClose={() => setOpenMenu(null)} maxWidth="lg" fullWidth>
          <DialogTitle variant="h6">Enter kit header details</DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Checkbox checked={zeroKitLineItems} onChange={e => setZeroKitLineItems(e.target.checked)} />
                  }
                  label="Zero kit line items"
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="Product Description"
                  fullWidth
                  value={productDescription}
                  size="small"
                  onChange={e => setProductDescription(e.target.value)}
                  slotProps={{ htmlInput: { maxLength: fieldLengths.lineItems.productDescription } }}
                />
              </Grid>
              <Grid size={3}>
                <TextField
                  label="MPN"
                  fullWidth
                  value={mpn}
                  size="small"
                  onChange={e => setMpn(e.target.value)}
                  slotProps={{ htmlInput: { maxLength: fieldLengths.lineItems.mfrPartNum } }}
                />
              </Grid>
              <Grid size={3}>
                <TextField
                  label="Display Part Number"
                  fullWidth
                  value={displayPartNumber}
                  size="small"
                  onChange={e => setDisplayPartNumber(e.target.value)}
                  slotProps={{ htmlInput: { maxLength: fieldLengths.lineItems.displayPartNumber } }}
                />
              </Grid>
              <Grid size={6}>
                <NumberField
                  label="Quantity"
                  fullWidth
                  value={quantity}
                  size="small"
                  onChange={value => setQuantity(value ?? 0)}
                />
              </Grid>
              <Grid size={6}>
                <CurrencyInput
                  label="Unit Price"
                  fullWidth
                  value={unitPrice}
                  size="small"
                  onChange={value => setUnitPrice(value ?? 0)}
                />
              </Grid>
              <Grid size={6}>
                <CurrencyInput
                  label="Total Price"
                  fullWidth
                  value={totalPrice}
                  size="small"
                  onChange={value => setTotalPrice(value ?? 0)}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setOpenMenu(null)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                props.onCreateNewKit?.(
                  productDescription ? productDescription.trim() : null,
                  mpn ? mpn.trim() : null,
                  quantity,
                  unitPrice,
                  totalPrice,
                  displayPartNumber,
                )
                setOpenMenu(null)
              }}
            >
              Create New Kit
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {!bulkOnly && (
        <Dialog open={openMenu === 'kit-warning'} onClose={() => setOpenMenu(null)} maxWidth="xs" fullWidth>
          <DialogTitle variant="h6">Break the kit?</DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              {props.selectedLineItems.filter(item => item.Kit).length} line item
              {props.selectedLineItems.filter(item => item.Kit).length > 1 ? 's are' : ' is'} already in a kit. Are you
              sure you want to break the kit and create a new one with the selected items?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setOpenMenu(null)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setOpenMenu('kit-create')
              }}
            >
              Yes, proceed
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  )
}
